import isEqual from 'lodash/isEqual';
import React, {
  useState, useEffect, useMemo, useCallback, useRef
} from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Typography,
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useNavigate } from '@thd-olt-component-react/router';
import { Row, Placeholder } from '@thd-olt-component-react/core-ui';
import { func, bool, string } from 'prop-types';
import {
  QueryProvider
} from '@thd-nucleus/data-sources';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import { DynamicComponent } from '@thd-nucleus/app-render';
import { getLocalizerZipCode } from '../../../../utils/fulfillment-utils';
import { useCabinetVisualizerStore, useCabinetVisualizerDigitalDataStore } from '../../../../store';
import { AdvancedFilters as Filters } from './CabinetDrawerComponents/filters';
import { useGetCollections } from '../../../../pages/cabinetVisualizer/hooks/useGetCollections';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import '../../../../styles/cabinetVisualizer/cabinetCollectionDrawer.css';
import {
  getFiltersFromLocalStorage,
  saveCabinetsCategoryAndFiltersInLocalStorage
} from '../../../../pages/collection/common';
import { createTempDesign } from '../../../../utils/createTempDesign';
import { CABINET_VISUALIZER_DIGITAL_DATA } from '../../../../constants';
import { extractProductNameFromUrl } from './extractProductNameFromUrl';
import ResponsiveCabinetCollectionCard from './CabinetDrawerComponents/ResponsiveCabinetCollectionCard';
import DrawerTipMessage from '../../../Drawer/DrawerTipMessage';
import { useDrawerStore } from '../../../../store/ui';
import { getProductIdFromPath } from '../../../../utils/queryParams';

export const CabinetCollectionDrawer = React.memo(({
  designId,
  open,
  onClose,
  onSelectCollection,
}) => {
  const store = useStore();
  const { storeId } = store;
  const zipCode = getLocalizerZipCode();
  const isMobile = useIsMobileDevice();
  const {
    trackCabinetCollectionSelectionOverlayEvent,
    trackCollectionViewDetailsEvent,
    trackCollectionRefinementsFiltersEvent,
    trackCabinetCollectionSelectionInteractionEvent,
  } = useCabinetVisualizerDigitalDataStore();
  const hasTrackedRef = useRef(false);
  const [drawerElement, setDrawerElement] = useState(null);
  const drawerCallbackRef = useCallback((node) => {
    setDrawerElement(node);
  }, []);
  const productIdFromPath = getProductIdFromPath();
  const shouldShowCloseButton = !!productIdFromPath;

  useEffect(() => {
    if (open && !hasTrackedRef.current) {
      trackCabinetCollectionSelectionOverlayEvent({
        eventName: CABINET_VISUALIZER_DIGITAL_DATA.CONFIGURATOR.CHOOSE_CABINET_COLLECTION
      });
      hasTrackedRef.current = true;
    }
    if (!open) {
      hasTrackedRef.current = false;
    }
  }, [open]);

  const navigate = useNavigate();
  const {
    cabinetVisualizer,
    updateCabinetVisualizer,
    setUserSelectedCollection,
    prevAnalyticsFilterGroups = {},
    setPrevAnalyticsFilterGroups,
    hasFiredInitialRefinementsEvent,
    setHasFiredInitialRefinementsEvent,
  } = useCabinetVisualizerStore();
  const drawerTitle = 'Choose Cabinet Collection';
  const rawHost = useConfigService('global-custom-commerce:host');
  const host = useMemo(() => rawHost, [rawHost]);
  const collectionCardContainerClasses = classNames(
    'sui-grid sui-w-full sui-gap-4 sui-px-1 sui-max-w-full sui-box-border',
    'sui-grid-cols-1',
    'sm:sui-grid-cols-[repeat(auto-fill,minmax(min(500px,100%),1fr))]'
  );

  const omsId = '334219672';
  const [dataSource, setDataSource] = useState(null);
  const [filteredDataSource, setFilteredDataSource] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    filters: [],
    filterNames: []
  });
  const collections = useDrawerStore((state) => state.collections);
  const getLeftViewImageUrl = useDrawerStore((state) => state.getLeftViewImageUrl);
  const setSelectedCollection = useDrawerStore((state) => state.setSelectedCollection);

  const [primaryFilters, setPrimaryFilters] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(getFiltersFromLocalStorage(omsId) || []);

  const [selectedCategory, setSelectedCategory] = useState(''); // getCategoryFromLocalStorage(omsId)
  const [analyticsFilterGroups, setAnalyticsFilterGroups] = useState(null);

  /**
   * Handles applying filters to data source for the cabinet collection drawer.
   *
   * This function processes the selected filters, groups them by filter name,
   * retrieves the corresponding itemIds for each filter type (e.g., Brand Name, Color Finish Family, Door Style),
   * and filters the dataSource by the intersection of all active filter itemIds.
   * The filtered results are then set to the filteredDataSource state, and the selected filters
   * are stored in the advancedFilters state.
   *
   * @param {string[]} selectedFilters - An array of selected filter strings in the format "Filter Name - Filter Value".
   * @param {object[]} defaultDataSource - optional data source to filter. If not provided, uses the current dataSource.
   * @param {object[]} defaultFilters - optional default filters to use, if not provided, uses primaryFilters.
   *
   * Example:
   *   selectedFilters = [
   *     "Brand Name - Contractor Express",
   *     "Brand Name - J Collection",
   *     "Color Finish Family - White"
   *   ]
   *
   * The function supports multiple selections for each filter type and ensures that only items
   * matching all selected filter criteria are shown.
   */
  const applyFiltersToDataSource = (selectedFilters, defaultDataSource, defaultFilters) => {
    // Group selected values by filter name
    const filterGroups = selectedFilters.reduce((acc, filter) => {
      const [filterName, filterValue] = filter.split(' - ');
      if (!acc[filterName]) acc[filterName] = [];
      acc[filterName].push(filterValue);
      return acc;
    }, {});

    setAnalyticsFilterGroups(filterGroups);

    const filters = defaultFilters || primaryFilters;
    // Helper to get all itemIds for a filter type (union of all selected values)
    const getItemIdsForFilter = (filterLabel, selectedValues) => {
      const filter = filters.find((fl) => fl.label === filterLabel);
      if (!filter) return [];
      return filter.refinements
        .filter((refinement) => selectedValues.includes(refinement.label))
        .flatMap((refinement) => refinement.itemIds);
    };

    // Get itemIds for each filter type
    const brandItemIds = filterGroups['Brand Name']
      ? getItemIdsForFilter('Brand Name', filterGroups['Brand Name'])
      : null;
    const colorItemIds = filterGroups['Color Finish Family']
      ? getItemIdsForFilter(
        'Color Finish Family',
        filterGroups['Color Finish Family']
      )
      : null;
    const doorStyleItemIds = filterGroups['Door Style']
      ? getItemIdsForFilter('Door Style', filterGroups['Door Style'])
      : null;

    // Filter dataSource by intersection of all active filter itemIds
    let filteredData = (defaultDataSource || dataSource);
    const activeFilters = [brandItemIds, colorItemIds, doorStyleItemIds].filter(Boolean);

    if (activeFilters.length > 0) {
      const commonItemIds = activeFilters.reduce((acc, ids) => acc.filter((id) => ids.includes(id))
      );
      filteredData = (defaultDataSource || dataSource).filter((item) => commonItemIds.includes(item.itemId));
    }
    setFilteredDataSource(filteredData);
  };

  const lastAnalyticsFilterGroupsRef = useRef(prevAnalyticsFilterGroups);

  useEffect(() => {

    const isAnalyticsGroupObject = analyticsFilterGroups
      && typeof analyticsFilterGroups === 'object'
      && !Array.isArray(analyticsFilterGroups);

    // Initial fire: delay analytics event
    const hasSavedFilters = isAnalyticsGroupObject && Object.keys(analyticsFilterGroups).length > 0;
    if (
      isAnalyticsGroupObject
      && !hasFiredInitialRefinementsEvent
    ) {
      if (hasSavedFilters) {
        // do not fire empty even on load
        trackCollectionRefinementsFiltersEvent({
          filterGroups: analyticsFilterGroups,
          delayed: true
        });
      }
      setHasFiredInitialRefinementsEvent(true);
      setPrevAnalyticsFilterGroups(analyticsFilterGroups);
      return;
    }

    // User-initiated filter changes (no delay)
    if (
      isAnalyticsGroupObject
      && hasFiredInitialRefinementsEvent
      && !isEqual(prevAnalyticsFilterGroups, analyticsFilterGroups)
      && !isEqual(lastAnalyticsFilterGroupsRef.current, analyticsFilterGroups)
    ) {
      trackCollectionRefinementsFiltersEvent({
        filterGroups: analyticsFilterGroups,
        delayed: false
      });
      setPrevAnalyticsFilterGroups(analyticsFilterGroups);
      lastAnalyticsFilterGroupsRef.current = analyticsFilterGroups;
    }
  }, [
    analyticsFilterGroups,
    prevAnalyticsFilterGroups,
    hasFiredInitialRefinementsEvent,
    setPrevAnalyticsFilterGroups,
    trackCollectionRefinementsFiltersEvent
  ]);

  const updateAdvancedFilters = useCallback((event, selectedFilters) => {
    event.preventDefault();
    event.stopPropagation();

    saveCabinetsCategoryAndFiltersInLocalStorage(omsId, selectedCategory, selectedFilters);

    if (selectedFilters.length === 0) {
      setFilteredDataSource(null);
      setAdvancedFilters([]);
      setAnalyticsFilterGroups({}); // ensure new object reference
      return;
    }

    applyFiltersToDataSource(selectedFilters);
    setAdvancedFilters(selectedFilters);

  }, [dataSource, primaryFilters, collections, omsId, selectedCategory, trackCollectionRefinementsFiltersEvent]);

  const getProductDetailUrl = (canonicalUrl) => {
    if (canonicalUrl?.includes('/p/cabinet-seller/')) {
      return canonicalUrl;
    }
    const updatedCanonicalUrl = canonicalUrl?.replace('/p/', '/p/cabinet-seller/');
    return updatedCanonicalUrl || '#';
  };

  const getProductImageUrl = (product) => {
    if (product?.media?.images?.some((img) => img.subType === 'PRIMARY')) {
      return product.media.images.find((img) => img.subType === 'PRIMARY').url.replace('<SIZE>', '400');
    }
    if (product?.media?.images?.length > 0) {
      return product.media.images[0].url.replace('<SIZE>', '400');
    }
    return '';
  };

  const getSecondaryImageUrl = (product) => {
    if (product?.media?.images?.some((img) => img.type === 'IMAGE_LEFT_VIEW')) {
      return product.media.images.find((img) => img.type === 'IMAGE_LEFT_VIEW').url.replace('<SIZE>', '400');
    }
    return null;
  };

  const getBrandNameFilters = () => {
    const brandMap = collections?.products?.reduce((acc, product) => {
      const brand = product.brandName || 'N/A';
      const itemId = product.itemId;
      if (!acc[brand]) {
        acc[brand] = { itemIds: [], count: 0 };
      }
      acc[brand].itemIds.push(itemId);
      acc[brand].count += 1;
      return acc;
    }, {});

    // To get distinct brand names:
    const distinctBrandNames = Object.keys(brandMap);
    const displayLabel = isMobile ? 'Brand' : 'Brand Name';
    const brandFilters = { label: 'Brand Name', displayLabel, refinements: [] };
    brandFilters.refinements = distinctBrandNames.map((brand) => ({
      label: brand,
      recordCount: brandMap[brand].count,
      itemIds: brandMap[brand].itemIds
    }));

    return brandFilters;
  };

  const getColorFinishFilters = () => {
    const colorMap = collections?.products?.reduce((acc, product) => {
      const color = product.colorFinishFamily || 'N/A';
      const itemId = product.itemId;
      if (!acc[color]) {
        acc[color] = { itemIds: [], count: 0 };
      }
      acc[color].itemIds.push(itemId);
      acc[color].count += 1;
      return acc;
    }, {});

    // To get distinct color names:
    const distinctColorNames = Object.keys(colorMap);

    const displayLabel = isMobile ? 'Color' : 'Color Finish Family';
    const colorFilters = { label: 'Color Finish Family', displayLabel, refinements: [] };
    colorFilters.refinements = distinctColorNames.map((color) => ({
      label: color,
      recordCount: colorMap[color].count,
      itemIds: colorMap[color].itemIds
    }));

    return colorFilters;
  };

  useEffect(() => {
    if (collections && collections.products && !dataSource) {
      // Transform JSON data to match component expectations
      const newDataSource = collections?.products?.map((product) => ({
        productLabel: product.productLabel || 'N/A',
        brandName: product.brandName || 'N/A',
        productURL: getProductDetailUrl(product.canonicalUrl),
        primaryURL: getProductImageUrl(product),
        secondaryURL: getSecondaryImageUrl(product),
        designStyleChoiceId: product.designStyleChoiceId,
        itemId: product.itemId,
        leftViewImageUrl: getLeftViewImageUrl(product),
        colorFinish: product.colorFinish || product.color,
        startingAtPrice: product.startingAtPrice,
        sampleBaseCabinetItemId: product.sampleBaseCabinetItemId,
      })) || [];

      setDataSource(newDataSource);

      const doorStyleFilters = collections.primaryFilters[0];
      doorStyleFilters.label = 'Door Style';
      doorStyleFilters.displayLabel = 'Door Style';
      const filters = [doorStyleFilters, getBrandNameFilters(), getColorFinishFilters()];
      setPrimaryFilters(filters);

      const filterNames = filters.map((filter) => {
        return { label: filter.label, displayLabel: filter.displayLabel };
      });
      setFilterOptions({
        filters,
        filterNames
      });

      if (advancedFilters && advancedFilters.length > 0) {
        applyFiltersToDataSource(advancedFilters, newDataSource, filters);
      } else {
        setAnalyticsFilterGroups({});
      }
    }
  }, [dataSource, collections]);

  // Utility to get canonicalUrl from collections by itemId
  const getProductCanonicalUrlByItemId = (itemId) => {
    if (!collections || !collections.products) return undefined;
    const product = collections.products.find((productData) => productData.itemId === itemId);
    return product ? product.canonicalUrl : undefined;
  };

  const handleSelectCollection = async (collection) => {
    setSelectedCollection(collection);

    const selectedStyleChoiceid = collection.designStyleChoiceId;
    const selectedItemId = collection.itemId || collection.omsId;
    // Parse the selected style choice ID as a number for consistent comparisons
    const numericStyleChoiceId = parseInt(selectedStyleChoiceid, 10);
    if (setUserSelectedCollection) {
      setUserSelectedCollection(numericStyleChoiceId);
    }
    // Get the current selected item ID from the URL or store
    const currentSelectedItemId = getProductIdFromPath() || cabinetVisualizer.selectedItemId;

    // Check if we're selecting a different collection (different itemId/omsId)
    const shouldClearExistingDesigns = selectedItemId !== currentSelectedItemId;

    // Update the URL with the selected collection
    const currentUrl = new URL(window.location);
    // Add or update the query param
    currentUrl.searchParams.set('DesignStyleChoiceid', selectedStyleChoiceid);
    // Add itemId or omsId to the URL path
    if (selectedItemId) {
      // Get current pathname without any existing numeric ID segments
      const currentPath = currentUrl.pathname;
      const basePathSegments = currentPath.split('/').filter((segment) => segment && !/^[0-9]+$/.test(segment));
      // Append the selected item ID to the path
      const newPath = `/${basePathSegments.join('/')}/${selectedItemId}`;
      currentUrl.pathname = newPath;
    }
    navigate(currentUrl.pathname + currentUrl.search, { replace: false });

    // Find all designs that match the selected style choice
    const designsMatchingNewStyle = cabinetVisualizer.designIds.filter((curId) => {
      const design = cabinetVisualizer.designs[curId];
      // Convert both to integers before comparison to handle string vs number issues
      return design && parseInt(design.DesignStyleChoiceid, 10) === numericStyleChoiceId;
    });

    // Start with current designs and designIds
    let updatedDesigns = { ...cabinetVisualizer.designs };
    let updatedDesignIds = [...cabinetVisualizer.designIds];
    let updatedStyleDesignIds = [];
    let newSelectedDesignId = selectedItemId;

    if (shouldClearExistingDesigns) {
    // Clear all existing designs when selecting a different collection
      updatedDesigns = {};
      updatedDesignIds = [];

      // Create new temp design
      const tempId = `temp-${numericStyleChoiceId}`;
      const tempDesign = createTempDesign(numericStyleChoiceId, tempId);
      updatedDesigns[tempId] = tempDesign;
      updatedDesignIds.push(tempId);
      updatedStyleDesignIds = [tempId];
      newSelectedDesignId = tempId;
    } else {
    // Same collection selected, keep existing designs but still update styleDesignIds
      updatedStyleDesignIds = [...cabinetVisualizer.styleDesignIds];
    }

    // When a collection is selected, ALWAYS set these flags to ensure it sticks:
    updateCabinetVisualizer({
      styleChoiceId: numericStyleChoiceId,
      userSelectedStyleChoice: true, // CRITICAL: This prevents guard effect from nullifying styleChoiceId
      isLandingPage: false,
      selectedDesignId: newSelectedDesignId,
      designs: updatedDesigns,
      designIds: updatedDesignIds,
      styleDesignIds: updatedStyleDesignIds,
      choices: null,
      choiceMetadata: null,
      skuDrawer: {
        ...cabinetVisualizer.skuDrawer,
        modalAssortment: {},
        superSkuProducts: [],
        selectedItemId: null,
        selectedParentId: null,
      },
      collectionChangeTriggered: true,
    });

    // Notify parent component about the selection
    if (onSelectCollection) {
      onSelectCollection(collection);
      const canonicalUrl = getProductCanonicalUrlByItemId(selectedItemId);
      const eventTarget = extractProductNameFromUrl(canonicalUrl);
      trackCabinetCollectionSelectionInteractionEvent({ target: eventTarget });
    }

    // Close this drawer to allow the next drawer to open
    if (onClose) {
      onClose(null); // Pass null to indicate this is not a user event
    }
  };

  const handleViewDetails = (productURL) => {
    const eventTarget = extractProductNameFromUrl(productURL);
    trackCollectionViewDetailsEvent({ target: eventTarget });
    window.open(productURL, '_blank', 'noopener,noreferrer');
  };

  // Create ref object for compatibility with DrawerTipMessage
  const drawerRef = useMemo(() => ({
    current: drawerElement
  }), [drawerElement]);

  if (!dataSource || dataSource.length === 0 || !filterOptions || filterOptions.filters.length === 0) {
    return (
      <Drawer open={open} position="right" onClose={onClose}>
        <DrawerHeader title={drawerTitle} onClose={onClose} />
        <DrawerBody>
          <Typography>Loading collections...</Typography>
        </DrawerBody>
        <DrawerFooter />
      </Drawer>
    );
  }
  return (
    <>
      {(!isMobile && !shouldShowCloseButton) && (
        <DrawerTipMessage
          show={open}
          title={(
            <>
              Select a cabinet<br />
              collection to build<br />
              your layout
            </>
          )}
          content=""
          drawerRef={drawerRef}
        />
      )}
      <Drawer
        open={open}
        position="right"
        onClose={shouldShowCloseButton ? (event) => onClose(event) : undefined}
        DrawerRootProps={{ style: { transition: 'right 0.3s ease-in 0s' } }}
      >
        <div className="cabinet-collection-drawer-component" ref={drawerCallbackRef}>

          <DrawerHeader
            title={drawerTitle}
            onClose={shouldShowCloseButton ? onClose : undefined}
          />

          <DrawerBody>
            <ErrorBoundary name="cabinet-drawer-eb">
              <Row>
                {/* Filters section */}
                <Filters
                  filterOptions={filterOptions}
                  advancedFilters={advancedFilters}
                  updateAdvancedFilters={updateAdvancedFilters}
                  selectedCategory={selectedCategory}
                  resultCount={filteredDataSource?.length || 0}
                />
              </Row>
              <div
                className={collectionCardContainerClasses}
              >
                {(filteredDataSource || dataSource).map((item, index) => (
                  <div
                    key={index}
                  >
                    <QueryProvider
                      dataSource="searchNav"
                      cacheKey="cabinet-collection-card"
                      defaultVariables={{ zipCode, storeId }}
                    >
                      <DynamicComponent pageType="collection">
                        <Hydrator
                          id={`collection-card-${item.itemId}`}
                          delay={0}
                          scrollBuffer={100}
                          placeholder={(
                            <Placeholder
                              width="100%"
                              height="300px"
                              color="#F5F5F5"
                            />
                          )}
                        >
                          <ResponsiveCabinetCollectionCard
                            collection={item}
                            hoverableMedia={!isMobile}
                            onPrimaryActionClick={handleSelectCollection}
                            onSecondaryActionClick={handleViewDetails}
                            primaryActionButtonLabel="Select Collection"
                            secondaryActionButtonLabel="View"
                            isMobile={isMobile}
                          />
                        </Hydrator>
                      </DynamicComponent>
                    </QueryProvider>
                  </div>
                ))}
              </div>
            </ErrorBoundary>
          </DrawerBody>
          <DrawerFooter />
        </div>
      </Drawer>
    </>

  );

});

CabinetCollectionDrawer.propTypes = {
  designId: string,
  open: bool.isRequired,
  onClose: func,
  onSelectCollection: func
};

CabinetCollectionDrawer.defaultProps = {
  designId: undefined,
  onClose: undefined,
  onSelectCollection: undefined
};
